import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { marketingUrl } from "@/lib/domains";
import { toast } from "sonner";
import { Loader2, Building2, Ticket } from "lucide-react";
import SupabaseAuthBlock from "@/components/app/SupabaseAuthBlock";
import GoogleAuthButton from "@/components/app/GoogleAuthButton";

export default function Signup() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { signup, loading } = useAuth();
  const [mode, setMode] = useState(params.get("invite") ? "invite" : "create");
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    clinic_name: "", invite_token: params.get("invite") || "",
  });
  const [invitePreview, setInvitePreview] = useState(null);

  useEffect(() => {
    const t = form.invite_token;
    if (mode === "invite" && t && t.length > 8) {
      api.get(`/invites/verify/${t}`)
        .then((r) => { setInvitePreview(r.data); if (r.data.email) setForm((f) => ({ ...f, email: r.data.email })); })
        .catch(() => setInvitePreview(null));
    } else {
      setInvitePreview(null);
    }
  }, [form.invite_token, mode]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, email: form.email, password: form.password };
      if (mode === "create") payload.clinic_name = form.clinic_name;
      else payload.invite_token = form.invite_token;
      await signup(payload);
      toast.success(mode === "create" ? "Clinic created" : "Joined clinic");
      nav("/app");
    } catch (err) {
      toast.error(apiError(err, "Signup failed"));
    }
  };

  return (
    <div className="min-h-screen bg-linen grid place-items-center p-8">
      <SEO title="Start your free trial" path="/signup" description="Start your 30-day free trial of Viyora — the modern dental clinic management SaaS with Viyora Copilot, WhatsApp reminders, and GST invoicing. No card required." />
      <form onSubmit={submit} className="w-full max-w-md card-widget" data-testid="signup-form">
        <a href={marketingUrl("/")} data-testid="brand-link" className="flex items-center gap-2 mb-6">
          <img src="/viyora-icon-64.png" alt="Viyora" className="w-9 h-9 " />
          <span className="font-display text-xl">Viyora</span>
        </a>

        <div className="flex gap-2 mb-6 bg-linen rounded-full p-1">
          <button type="button" data-testid="mode-create"
            onClick={() => setMode("create")}
            className={`flex-1 rounded-full px-4 py-2 text-xs font-medium inline-flex items-center justify-center gap-2 transition-all ${mode === "create" ? "bg-pine text-linen" : "text-ink-mid"}`}>
            <Building2 className="w-3.5 h-3.5" /> Create clinic
          </button>
          <button type="button" data-testid="mode-invite"
            onClick={() => setMode("invite")}
            className={`flex-1 rounded-full px-4 py-2 text-xs font-medium inline-flex items-center justify-center gap-2 transition-all ${mode === "invite" ? "bg-pine text-linen" : "text-ink-mid"}`}>
            <Ticket className="w-3.5 h-3.5" /> Join with invite
          </button>
        </div>

        <span className="overline text-ink-soft">{mode === "create" ? "Start a clinic" : "Accept invite"}</span>
        <h1 className="mt-2 font-display text-3xl">
          {mode === "create" ? "Onboard your clinic to Viyora." : "Join your team on Viyora."}
        </h1>

        <div className="mt-6 space-y-3">
          {mode === "create" && (
            <input required data-testid="signup-clinic-name" placeholder="Clinic name (e.g. Meridian Dental)"
              value={form.clinic_name} onChange={(e) => setForm({ ...form, clinic_name: e.target.value })}
              className="w-full rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
          )}
          {mode === "invite" && (
            <>
              <input required data-testid="signup-invite-token" placeholder="Invite token"
                value={form.invite_token} onChange={(e) => setForm({ ...form, invite_token: e.target.value })}
                className="w-full rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-pine/40" />
              {invitePreview && (
                <div className="rounded-xl bg-[#E8F0EB] text-[#2D6A4F] p-3 text-xs" data-testid="invite-preview">
                  Joining <strong>{invitePreview.clinic_name}</strong> as <strong>{invitePreview.role}</strong>.
                </div>
              )}
            </>
          )}

          <input required data-testid="signup-name" placeholder="Your full name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
          <input required data-testid="signup-email" type="email" placeholder="Email" value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
          <input required data-testid="signup-password" type="password" placeholder="Password (min 6 chars)" minLength={6}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full rounded-xl border border-[#E8EAE9] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pine/40" />
        </div>

        <button data-testid="signup-submit" disabled={loading} type="submit" className="mt-6 w-full btn-primary flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          {mode === "create" ? "Create clinic & continue" : "Join clinic"}
        </button>
        <p className="mt-4 text-sm text-ink-mid text-center">
          Already have an account? <Link to="/login" data-testid="link-login" className="text-ink font-medium hover:underline">Sign in</Link>
        </p>
      </form>

      {mode === "invite" && (
        <div className="w-full max-w-md mt-4">
          <div className="flex items-center gap-3 text-xs text-ink-soft my-6">
            <div className="flex-1 h-px bg-[#E8EAE9]" /> or <div className="flex-1 h-px bg-[#E8EAE9]" />
          </div>
          <GoogleAuthButton invite={form.invite_token} />
          <SupabaseAuthBlock mode="signup" />
        </div>
      )}
    </div>
  );
}
